import { ChevronRight, Hospital, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { useAuth } from "~/auth/auth-context";
import { hasAdminAccess } from "~/auth/ward-roles";
import { Card, CardContent } from "~/components/ui/card";
import { getMyWards, type WardDto } from "~/lib/api";
import NotFoundRoute from "~/routes/not-found";

// The admin landing — the target of the Shield "Admin" nav entry (E0.3). It
// lists the admin areas the signed-in user can reach: super-admins get
// ward management (E1.2), and anyone who administers a ward gets a link to that
// ward's team roster (E2.2). A user with no admin access at all can't reach this
// page — though the nav already hides it from them.
export default function AdminHomeRoute(): JSX.Element {
  const { isSuperAdmin, wards, isWardAdmin } = useAuth();
  const [myWards, setMyWards] = useState<WardDto[] | null>(null);

  // Resolve names for the wards this user administers — useAuth carries only
  // ids + roles, so the display names come from /wards (the caller is a member
  // of every ward they administer).
  useEffect(() => {
    if (wards.length === 0) return;
    let cancelled = false;
    void getMyWards().then((result) => {
      if (!cancelled) setMyWards(result.data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [wards.length]);

  if (!hasAdminAccess(isSuperAdmin, wards)) return <NotFoundRoute />;

  const adminWards = (myWards ?? []).filter((ward) => isWardAdmin(ward.id));

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
        <p className="text-sm text-muted-foreground">Manage your organisation&rsquo;s settings.</p>
      </header>

      {isSuperAdmin && (
        <ul className="space-y-3">
          <li>
            <Link
              to="/admin/wards"
              className="flex items-center gap-4 rounded-xl border border-border p-4 transition-colors hover:bg-accent"
            >
              <Hospital className="h-5 w-5 text-muted-foreground" aria-hidden />
              <span className="flex-1">
                <span className="block font-medium">Wards</span>
                <span className="block text-sm text-muted-foreground">
                  Create, edit, and archive wards.
                </span>
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            </Link>
          </li>
        </ul>
      )}

      {adminWards.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Your ward teams
          </h2>
          <ul className="space-y-3">
            {adminWards.map((ward) => (
              <li key={ward.id}>
                <Link
                  to={`/admin/wards/${ward.id}/members`}
                  className="flex items-center gap-4 rounded-xl border border-border p-4 transition-colors hover:bg-accent"
                >
                  <Users className="h-5 w-5 text-muted-foreground" aria-hidden />
                  <span className="flex-1">
                    <span className="block font-medium">{ward.name}</span>
                    <span className="block text-sm text-muted-foreground">
                      Manage this ward&rsquo;s team.
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isSuperAdmin && adminWards.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            There are no admin areas available to you yet.
          </CardContent>
        </Card>
      )}
    </section>
  );
}
